import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft } from '@fortawesome/pro-regular-svg-icons';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import styles from './SidebarModal.module.scss';

export interface SidebarModalItem {
    id: string;
    label: string;
    icon: IconDefinition;
    content: React.ReactNode;
}

interface SidebarModalProps {
    items: SidebarModalItem[];
    defaultSelectedId?: string;
    className?: string;
    sidebarClassName?: string;
    contentClassName?: string;
    prependSidebarContent?: React.ReactNode;
}

export const SidebarModal: React.FC<SidebarModalProps> = ({
    items,
    defaultSelectedId,
    className,
    sidebarClassName,
    contentClassName,
    prependSidebarContent
}) => {
    const [selectedItemId, setSelectedItemId] = useState<string>(defaultSelectedId || items[0]?.id || '');
    const [isMobile, setIsMobile] = useState(false);
    const [showContent, setShowContent] = useState(false);

    // Check if we're in mobile view
    useEffect(() => {
        const checkMobile = () => {
            const mobile = window.innerWidth <= 960;
            setIsMobile(mobile);
            // On desktop, always show content
            if (!mobile) {
                setShowContent(false);
            }
        };

        checkMobile();
        window.addEventListener('resize', checkMobile);

        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    const selectedItem = items.find(item => item.id === selectedItemId);

    const handleItemClick = (itemId: string) => {
        setSelectedItemId(itemId);
        if (isMobile) {
            setShowContent(true);
        }
    };

    const handleBackToSidebar = () => {
        setShowContent(false);
    };

    return (
        <div className={`${styles.sidebarModal} ${className || ''}`}>
            {/* Sidebar */}
            <aside
                className={`
                    ${styles.sidebar} 
                    ${sidebarClassName || ''}
                    ${isMobile && showContent ? styles.hiddenOnMobile : ''}
                `}
            >
                {prependSidebarContent}
                <nav className={styles.navigation}>
                    {items.map(item => (
                        <button
                            key={item.id}
                            className={`
                                ${styles.navItem} 
                                ${item.id === selectedItemId ? styles.active : ''}
                            `}
                            onClick={() => handleItemClick(item.id)}
                        >
                            <FontAwesomeIcon
                                icon={item.icon}
                                className={styles.navIcon}
                            />
                            <span className={styles.navLabel}>{item.label}</span>
                            {isMobile && (
                                <FontAwesomeIcon
                                    icon={faChevronLeft}
                                    className={styles.chevron}
                                />
                            )}
                        </button>
                    ))}
                </nav>
            </aside>

            {/* Content */}
            <main
                className={`
                    ${styles.content} 
                    ${contentClassName || ''}
                    ${isMobile && !showContent ? styles.hiddenOnMobile : ''}
                `}
            >
                {isMobile && showContent && (
                    <div className={styles.mobileHeader}>
                        <button
                            className={styles.backButton}
                            onClick={handleBackToSidebar}
                            aria-label="Go back"
                        >
                            <FontAwesomeIcon icon={faChevronLeft} />
                        </button>
                    </div>
                )}

                <div className={styles.contentWrapper}>{selectedItem?.content}</div>
            </main>
        </div>
    );
};

export default SidebarModal;
